/* eslint-disable @typescript-eslint/no-explicit-any */
import { SimulationCtx } from '@/contexts/Simulation/SimulationCtx';
import { AppDispatch, RootState } from '@/store';
import { createItem, deleteItem } from '@/store/storeSlice';
import { PersonnelGroup, TreeItem } from '@/store/types';
import { generateUID, hashNumber } from '@/utils/helpers';
import {
  Button,
  Checkbox,
  Label,
  Stack,
  Typography,
} from '@data-products-and-ai/react-components';
import { produce } from 'immer';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export type TFormPersonnelGroup = {
  PERSONNEL_GROUP_CLASSES: TreeItem<TFormPersonnelGroupClasses[]>;
  RESOURCE_ID: TreeItem;
  RESOURCE: TreeItem;
};

export type TFormPersonnelGroupClasses = {
  PERSONNEL_GROUP: TreeItem;
  isSelected?: boolean;
};

/**
 * FormPersonnel Component
 * Manages the form for editing a distinct personnel details within a simulation scenario
 * Handles form submission, input changes, and closing the form drawer
 *
 * @returns {JSX.Element} - The rendered component
 */
const FormPersonnel = () => {
  const { simulationParams, setSimulationParams } = useContext(SimulationCtx);
  const dispatch: AppDispatch = useDispatch();
  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected === true),
  );

  const InitialFormProducts: TFormPersonnelGroup = {
    PERSONNEL_GROUP_CLASSES: { value: [], originalValue: [] },
    RESOURCE_ID: { value: '', originalValue: '' },
    RESOURCE: { value: '', originalValue: '' },
  };

  const [deletedClasses, setDeletedClasses] = useState<
    TFormPersonnelGroupClasses[]
  >([]);
  const [addedClasses, setAddedClasses] = useState<
    TFormPersonnelGroupClasses[]
  >([]);
  const [form, setForm] = useState(InitialFormProducts);

  const WipArea =
    simulationParams.formArea === 'resource_sub_classes'
      ? simulationParams.formArea
      : 'resource_classes';

  const SelectedItemName =
    simulationParams.formArea === 'resource_sub_classes'
      ? 'RESOURCE_SUBCLASS'
      : 'RESOURCE_CLASS';
  const SelectedItemId =
    simulationParams.formArea === 'resource_sub_classes'
      ? 'RESOURCE_SUBCLASS_ID'
      : 'RESOURCE_CLASS_ID';

  const selectedItem =
    simulationParams.formItem && simulationParams.formArea && selectedScenario
      ? selectedScenario.data[WipArea].byId[simulationParams.formItem]
      : null;

  const generateId = (item: any) => {
    const uid = generateUID();
    const newRootId = hashNumber(item[SelectedItemId] + uid);
    return newRootId;
  };

  const getPersonnelGroups = (): TFormPersonnelGroupClasses[] => {
    const personnelGroups: Set<string> = new Set();
    selectedScenario?.data.personnel_group.allIds.map((itemId: string) => {
      const item = selectedScenario.data.personnel_group.byId[itemId];

      personnelGroups.add(item.PERSONNEL_GROUP.value);
    });

    return Array.from(personnelGroups)
      .map((item) => {
        if (selectedScenario === undefined) return undefined;
        const groups = selectedScenario?.data.personnel_group.allIds.filter(
          (itemId: string) => {
            const personnel_group =
              selectedScenario.data.personnel_group.byId[itemId];
            return personnel_group.PERSONNEL_GROUP.value === item;
          },
        );

        const isSelected = groups?.find((groupId: string) => {
          const personnel_group =
            selectedScenario?.data.personnel_group.byId[groupId];
          return (
            (selectedItem as any)[SelectedItemId]?.value ===
            personnel_group[SelectedItemId]?.value
          );
        })
          ? true
          : false;

        return {
          PERSONNEL_GROUP: {
            value: item,
            originalValue: item,
          },
          isSelected: isSelected ?? false,
        } as TFormPersonnelGroupClasses;
      })
      .filter((item): item is TFormPersonnelGroupClasses => item !== undefined);
  };

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { id, value, checked } = event.target as HTMLInputElement & {
      id: string;
      value: string;
      type: string;
      checked: boolean;
    };
    const updateForm = (id: keyof TFormPersonnelGroup, newValue: unknown) => {
      setForm((prevForm) => ({
        ...prevForm,
        [id]: {
          ...prevForm[id],
          value: newValue,
        },
      }));
    };

    const personnelGroup: TFormPersonnelGroupClasses = {
      PERSONNEL_GROUP: {
        value: id,
        originalValue: id,
      },
      isSelected: checked,
    };

    // i need to check if is class or subclass to add to the personnel group
    // pelo que percebi o personnel group classes é apenas especifico a subclasses, e tem tbm a class a qual a subclass pertence

    if (!checked) {
      setDeletedClasses((prev) => [...prev, personnelGroup]);
      setAddedClasses((prev) =>
        prev.filter((item) => item.PERSONNEL_GROUP.value !== value),
      );
    } else {
      setAddedClasses((prev) => [...prev, personnelGroup]);
      setDeletedClasses((prev) =>
        prev.filter((item) => item.PERSONNEL_GROUP.value !== value),
      );
    }
    const personnel_groups = form.PERSONNEL_GROUP_CLASSES.value.map(
      (item: TFormPersonnelGroupClasses) => {
        if (item.PERSONNEL_GROUP.value === value) {
          item.isSelected = checked;
        }
        return item;
      },
    );
    updateForm(
      'PERSONNEL_GROUP_CLASSES' as keyof TFormPersonnelGroup,
      personnel_groups,
    );
  };

  const handleCloseDrawer = () => {
    setSimulationParams(
      produce((draft) => {
        draft.formItem = undefined;
        draft.formArea = undefined;
        draft.drawerOpen = false;
      }),
    );

    setForm(InitialFormProducts);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!selectedItem || !selectedScenario) return;
    e.preventDefault();

    if (
      form.PERSONNEL_GROUP_CLASSES.value.filter(
        (item: TFormPersonnelGroupClasses) => item.isSelected,
      ).length === 0
    ) {
      alert('Please select at least one personnel group.');
      return;
    }

    for (const groupClass of deletedClasses) {
      const group = selectedScenario.data.personnel_group.allIds.find(
        (itemId: string) => {
          const personnel_group =
            selectedScenario.data.personnel_group.byId[itemId];
          return (
            personnel_group.PERSONNEL_GROUP.value ===
              groupClass.PERSONNEL_GROUP.value &&
            personnel_group[SelectedItemId].value ===
              (selectedItem as any)[SelectedItemId].value
          );
        },
      );
      if (group !== undefined) {
        dispatch(
          deleteItem({
            area: 'personnel_group',
            id: group,
          }),
        );
      }
    }

    for (const groupClass of addedClasses) {
      //not sure se aqui tenho que ver se é subclass ou class e dai ir buscar a resource tendo em conta o id correto
      const resourceId = selectedScenario.data.resources.allIds.find(
        (itemId: string) => {
          const resourceItem =
            selectedScenario.data.resources.byId[itemId];
          return (
            resourceItem[SelectedItemId].value ===
              (selectedItem as any)[SelectedItemId].value
          );
        });
      if (!resourceId) {
        return;
      }
      const resource = selectedScenario.data.resources.byId[resourceId];
      const PERSONNEL_GROUP_PK = generateId(groupClass);
      const site = selectedScenario.data.sites.byId[resource.SITE_ID.value];
      const personnelGroupInfo = selectedScenario.data.personnel_group.byId[
        selectedScenario.data.personnel_group.allIds[0]];

      const personnelGroup: PersonnelGroup = {
        PERSONNEL_GROUP_PK: {
          value: PERSONNEL_GROUP_PK,
          originalValue: PERSONNEL_GROUP_PK,
          type: 'text',
        },
        PERSONNEL_GROUP_ID: {
          value: generateUID(),
          originalValue: generateUID(),
          type: 'id',
        },
        PERSONNEL_GROUP: {
          value: groupClass.PERSONNEL_GROUP.value,
          originalValue: groupClass.PERSONNEL_GROUP.value,
          type: 'text',
        },
        RESOURCE_ID: {
          value: resource.RESOURCE_ID.value,
          originalValue: resource.RESOURCE_ID.value,
          type: 'text',
        },
        RESOURCE_CLASS_ID: {
          value: resource.RESOURCE_CLASS_ID.value,
          originalValue: resource.RESOURCE_CLASS_ID.value,
          type: 'text',
        },
        RESOURCE_SUBCLASS_ID: {
          value: resource.RESOURCE_SUBCLASS_ID.value,
          originalValue: resource.RESOURCE_SUBCLASS_ID.value,
          type: 'text',
        },
        SITE_ID: {
          value: site.SITE_ID.value,
          originalValue: site.SITE_ID.value,
          type: 'text',
        },
        SITE: {
          value: site.SITE.value,
          originalValue: site.SITE.value,
          type: 'id',
        },
        SITE_ABBREVIATION: {
          value: site.SITE_ABBREVIATION.value,
          originalValue: site.SITE_ABBREVIATION.value,
          type: 'id',
        },
        WIP_ID: {
          value: personnelGroupInfo.WIP_ID.value,
          type: 'id',
          originalValue: generateUID(),
        },
        key: '',
        title: '',
        is_disabled: false,
        is_wip: false,
        is_deleted: false,
        is_clicked: false,
        is_selectable: false,
        is_selected: false,
        is_hidden: false,
        is_match: false,
        is_open: false,
        parent: '',
        children: []
      } 
      if (personnelGroup) {
        dispatch(
          createItem({
            key: 'PERSONNEL_GROUP_PK',
            area: 'personnel_group',
            content: personnelGroup,
          }),
        );
      }
    }
    handleCloseDrawer();
  };

  useEffect(() => {
    if (!selectedItem || !selectedScenario) return;
    const groupClasses = getPersonnelGroups();

    const formToChange: TFormPersonnelGroup = {
      RESOURCE: {
        value: (selectedItem as any)[SelectedItemName].value,
        originalValue: (selectedItem as any)[SelectedItemName].value,
      },
      RESOURCE_ID: {
        value: (selectedItem as any)[SelectedItemId].value,
        originalValue: (selectedItem as any)[SelectedItemId].value,
      },
      PERSONNEL_GROUP_CLASSES: {
        value: groupClasses,
        originalValue: groupClasses,
      },
    };
    setForm(formToChange);
  }, [selectedItem]);

  if (!selectedItem || !selectedScenario) return;

  const formMargin = 30;

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: 20,
          paddingBottom: 100,
        }}
      >
        <Typography tag="h4" color="#8a00e5"> Edit Work Unit Personnel Group</Typography>
        <div
          style={{
            marginBottom: formMargin,
            marginTop: formMargin / 2,
          }}
        >
          <div>
            <Label tag="textdefault_strong">Work Unit </Label>
            <Typography tag="textdefault">
              {(selectedItem as any)[SelectedItemName].value}
            </Typography>
          </div>
        </div>
        <Label marginTop={formMargin} tag="textsmall_strong">
          Personnel Groups
        </Label>
        <Typography tag="textextrasmall">Select at least one!</Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: formMargin / 2,
          }}
        >
          {form.PERSONNEL_GROUP_CLASSES.value.map(
            (item: TFormPersonnelGroupClasses) => {
              return (
                <Checkbox
                  key={item.PERSONNEL_GROUP.value}
                  id={item.PERSONNEL_GROUP.value}
                  label={item.PERSONNEL_GROUP.value}
                  value={item.PERSONNEL_GROUP.value}
                  isChecked={item.isSelected}
                  onChange={(event) => {
                    handleInputChange(event);
                  }}
                />
              );
            },
          )}
        </div>
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            marginTop: 30,
            backgroundColor: 'white',
            paddingBottom: 20,
            paddingTop: 20,
            width: `calc(100% - 40px)`,
            borderTop: 'solid 1px #ededed',
          }}
        >
          <Stack direction="row" distribute="space-around">
            <Button
              buttonType="button"
              type="primaryOutline"
              width="200px"
              onClick={handleCloseDrawer}
            >
              Cancel
            </Button>
            <Button buttonType="submit" type="primaryNoShadow" width="200px">
              Update
            </Button>
          </Stack>
        </div>
      </div>
    </form>
  );
};

export default FormPersonnel;
